import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Mail, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(t('forgot_password.enter_email'));
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success(t('forgot_password.reset_sent'));
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <CardTitle className="text-xl">{t('forgot_password.check_email')}</CardTitle>
            <CardDescription>
              {t('forgot_password.email_sent_message')} <strong>{email}</strong>, {t('forgot_password.email_sent_suffix')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg text-sm text-gray-600">
              <p className="font-medium text-gray-900 mb-2">{t('forgot_password.didnt_receive')}</p>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('forgot_password.check_spam')}</li>
                <li>{t('forgot_password.check_email_correct')}</li>
                <li>{t('forgot_password.wait_retry')}</li>
              </ul>
            </div>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setSubmitted(false)}
            >
              {t('forgot_password.try_another')}
            </Button>
            
            <div className="text-center">
              <Link to="/login" className="text-sm text-emerald-600 hover:text-emerald-700">
                <ArrowLeft className="w-4 h-4 inline mr-1" />
                {t('forgot_password.back_to_login')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Request form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-6 h-6 text-emerald-600" />
          </div>
          <CardTitle className="text-xl">{t('forgot_password.title')}</CardTitle>
          <CardDescription>
            {t('forgot_password.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('forgot_password.email_address')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('forgot_password.email_placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-password-email"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
              data-testid="forgot-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('forgot_password.sending')}
                </>
              ) : (
                t('forgot_password.send_reset_link')
              )}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-emerald-600 hover:text-emerald-700">
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              {t('forgot_password.back_to_login')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
